import {
  emptyLegacy,
  emptyMods,
  MOD_KEYS,
  MOD_LABELS,
  type LegacyMods,
  type ModFlags,
  type ModKey,
} from "../../../shared/mods";
import { CATALOG, COLLECTIONS, type Collection } from "../../../shared/shop-catalog";
import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from "../../config";
import { drawSceneBackground, fillRoundedRect, strokeRoundedRect } from "../../utils/draw";
import type { Renderer } from "../Renderer";
import type { Button } from "../systems/Buttons";
import { backButton } from "../systems/Buttons";
import { ADMIN_NAME, Mods } from "../systems/Mods";
import { MenuScene } from "./MenuScene";
import type { GameContext, Scene } from "./Scene";

const PANEL_W = 312;
const CARD_X = (VIRTUAL_WIDTH - PANEL_W) / 2;
const HEADER_BOTTOM = 168;
const FOOTER_TOP = VIRTUAL_HEIGHT - 24;
const LIST_H = FOOTER_TOP - HEADER_BOTTOM;
const ROW_H = 26;
const ROW_GAP = 4;

type TargetState =
  | { kind: "self" }
  | { kind: "other"; name: string; flags: ModFlags; legacy: LegacyMods };

export class ModScene implements Scene {
  private target: TargetState = { kind: "self" };
  private view: "main" | "collections" = "main";
  private scrollY = 0;
  private maxScroll = 0;
  private dragLastY: number | null = null;
  private renderer: Renderer;

  constructor(
    private readonly ctxMgr: GameContext,
    private readonly returnTo?: Scene,
  ) {
    this.renderer = ctxMgr.renderer;
  }

  enter(): void {
    this.rebuild();
    const canvas = this.renderer.canvas;
    canvas.addEventListener("wheel", this.onWheel, { passive: false });
    canvas.addEventListener("pointerdown", this.onDragStart);
    window.addEventListener("pointermove", this.onDragMove);
    window.addEventListener("pointerup", this.onDragEnd);
  }

  exit(): void {
    this.ctxMgr.buttons.clear();
    const canvas = this.renderer.canvas;
    canvas.removeEventListener("wheel", this.onWheel);
    canvas.removeEventListener("pointerdown", this.onDragStart);
    window.removeEventListener("pointermove", this.onDragMove);
    window.removeEventListener("pointerup", this.onDragEnd);
  }

  update(_dt: number): void {
    // static
  }

  render(ctx: CanvasRenderingContext2D, _alpha: number): void {
    drawSceneBackground(ctx);

    ctx.fillStyle = "#f5e9c8";
    ctx.font = "bold 22px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(this.view === "collections" ? "GIVE COLLECTION" : "MOD MENU", VIRTUAL_WIDTH / 2, 38);

    ctx.font = "11px system-ui, sans-serif";
    ctx.fillText(`as ${this.ctxMgr.settings.playerName || ADMIN_NAME}`, VIRTUAL_WIDTH / 2, 56);

    // Build identity — tells you which deploy this client is running, so a
    // victim stuck on a stale bundle is easy to spot.
    ctx.font = "9px system-ui, sans-serif";
    ctx.fillStyle = "rgba(245,233,200,0.5)";
    ctx.textAlign = "right";
    ctx.fillText(`build ${__APP_VERSION__} · ${__BUILD_TIME__}`, VIRTUAL_WIDTH - 8, 14);
    ctx.fillStyle = "#f5e9c8";
    ctx.textAlign = "center";

    ctx.font = "11px system-ui, sans-serif";
    const targetLabel =
      this.target.kind === "self" ? "self" : `→ ${this.target.name}`;
    ctx.fillText(`Target: ${targetLabel}`, VIRTUAL_WIDTH / 2, 100);

    // scroll indicator
    if (this.maxScroll > 0) {
      const trackX = CARD_X + PANEL_W + 4;
      const trackH = LIST_H;
      const thumbH = Math.max(20, (LIST_H / (this.maxScroll + LIST_H)) * trackH);
      const t = this.scrollY / this.maxScroll;
      const thumbY = HEADER_BOTTOM + t * (trackH - thumbH);
      ctx.fillStyle = "rgba(245,233,200,0.25)";
      ctx.fillRect(trackX, HEADER_BOTTOM, 3, trackH);
      ctx.fillStyle = "rgba(245,233,200,0.85)";
      ctx.fillRect(trackX, thumbY, 3, thumbH);
    }

    ctx.fillStyle = "#f5e9c8";
    ctx.font = "11px system-ui, sans-serif";
    ctx.fillText(
      this.view === "collections"
        ? "tap a collection to GIVE it to the target · back to return"
        : "scroll for more pranks · tap a row to toggle",
      VIRTUAL_WIDTH / 2,
      FOOTER_TOP + 12,
    );
  }

  // Back closes the Give Collection sub-view first, then leaves the scene.
  private onBackButton(): void {
    if (this.view === "collections") {
      this.view = "main";
      this.scrollY = 0;
      this.rebuild();
      return;
    }
    this.goBack();
  }

  private openCollections(): void {
    this.view = "collections";
    this.scrollY = 0;
    this.rebuild();
  }

  private goBack(): void {
    this.ctxMgr.goTo(this.returnTo ?? new MenuScene(this.ctxMgr));
  }

  private currentFlags(): ModFlags {
    if (this.target.kind === "self") return this.ctxMgr.mods.flags;
    return this.target.flags;
  }

  /** Push the current "other" target's full payload (grief flags + legacy). */
  private pushTarget(): Promise<{ ok: boolean; error?: string }> {
    if (this.target.kind !== "other") return Promise.resolve({ ok: true });
    return Mods.pushTo(this.ctxMgr.settings.playerName, this.target.name, {
      flags: this.target.flags,
      legacy: this.target.legacy,
    });
  }

  private alertPushError(error: string | undefined, name: string): void {
    window.alert(
      error === "target_not_found"
        ? `Couldn't find "${name}". Reverting.`
        : "Couldn't update target's mods.",
    );
  }

  private async setFlag(key: ModKey, value: boolean): Promise<void> {
    // Jumpscare fires instantly on the LOCAL screen only when the target is yourself
    // (so you can test it, bypassing owner immunity). When another player is the
    // target it must hit THEM, not you — so don't fire locally; the push below
    // delivers it to the victim, who gets jumpscared during their game.
    if (key === "jumpscare" && value && this.target.kind === "self") {
      this.ctxMgr.jumpscare.fire(true);
    }
    if (this.target.kind === "self") {
      this.ctxMgr.mods.setFlag(key, value);
      this.rebuild();
      return;
    }
    const t = this.target;
    t.flags[key] = value;
    this.rebuild();
    const res = await this.pushTarget();
    if (!res.ok) {
      t.flags[key] = !value;
      this.rebuild();
      this.alertPushError(res.error, t.name);
      return;
    }
    // The jumpscare push isn't visible to you (it hits the target), so confirm it.
    if (key === "jumpscare" && value) {
      window.alert(`Jumpscare sent to ${t.name}! They'll get it within ~30s (instantly if they're in the game).`);
    }
  }

  private async toggleNoclip(): Promise<void> {
    if (this.target.kind === "self") {
      this.ctxMgr.mods.noclip = !this.ctxMgr.mods.noclip;
      this.rebuild();
      return;
    }
    const t = this.target;
    t.legacy.noclip = !t.legacy.noclip;
    this.rebuild();
    const res = await this.pushTarget();
    if (!res.ok) {
      t.legacy.noclip = !t.legacy.noclip;
      this.rebuild();
      this.alertPushError(res.error, t.name);
    }
  }

  private async chooseTarget(): Promise<void> {
    const raw = window.prompt("Apply mods to (player name, empty = self):", "") ?? "";
    const name = raw.trim();
    if (!name) {
      this.target = { kind: "self" };
      this.rebuild();
      return;
    }
    const remote = await Mods.fetchTarget(name);
    if (remote === null) {
      const create = window.confirm(`No data for "${name}". Push fresh mods anyway?`);
      if (!create) return;
      this.target = { kind: "other", name, flags: emptyMods(), legacy: emptyLegacy() };
    } else {
      this.target = { kind: "other", name, flags: remote.flags, legacy: remote.legacy };
    }
    this.scrollY = 0;
    this.rebuild();
  }

  // Owner-only: grant or revoke another player's mod-menu access.
  private giveTakeModMenu(): void {
    const raw = window.prompt("Give/Take the mod menu — player name:", "") ?? "";
    const name = raw.trim();
    if (!name) return;
    const give = window.confirm(
      `OK = GIVE the mod menu to "${name}".\nCancel = TAKE it away from "${name}".`,
    );
    void Mods.setAdmin(this.ctxMgr.settings.playerName, name, give).then((res) => {
      if (res.ok) {
        window.alert(`${give ? "Gave" : "Took"} the mod menu ${give ? "to" : "from"} "${name}".`);
      } else {
        window.alert(
          res.error === "target_not_found"
            ? `Couldn't find "${name}".`
            : res.error === "cannot_change_owner"
              ? "You can't change the owner's access."
              : `Couldn't update mod menu access (${res.error ?? "error"}).`,
        );
      }
    });
  }

  // Owner-only: revoke the mod menu from everyone except the owner.
  private revokeAllModMenus(): void {
    if (!window.confirm(`Take the mod menu from EVERYONE except "${ADMIN_NAME}"?`)) return;
    void Mods.revokeAdmins(this.ctxMgr.settings.playerName).then((res) => {
      window.alert(
        res.ok
          ? `Mod menu revoked from ${res.revoked ?? 0} player(s).`
          : `Couldn't revoke (${res.error ?? "error"}).`,
      );
    });
  }

  // Owner-only: claw back every admin-granted item from everyone except the owner.
  // Items players bought with flies or earned themselves stay put.
  private revokeAllGrants(): void {
    if (
      !window.confirm(
        `Take back ALL admin-granted items from EVERYONE except "${ADMIN_NAME}"?\n` +
          `Items players bought with flies or earned by playing are kept.\n` +
          `CREATOR & CUSTOM are always removed (even older grants); other items\n` +
          `only if they were granted after grant-tracking shipped.`,
      )
    ) {
      return;
    }
    void Mods.revokeGrants(this.ctxMgr.settings.playerName).then((res) => {
      window.alert(
        res.ok
          ? `Granted items clawed back from ${res.revoked ?? 0} player(s).`
          : `Couldn't revoke grants (${res.error ?? "error"}).`,
      );
    });
  }

  private async setCollection(coll: Collection, give: boolean): Promise<void> {
    const targetName =
      this.target.kind === "self" ? this.ctxMgr.settings.playerName : this.target.name;
    if (!targetName) {
      window.alert("No target set. Pick a target or log in.");
      return;
    }
    const verb = give ? "Give" : "Take";
    if (!window.confirm(`${verb} the "${coll.name}" collection ${give ? "to" : "from"} "${targetName}"?`))
      return;
    // Grant/revoke every item in the collection in ONE atomic request — firing a
    // request per item concurrently races on the server (lost writes).
    const res = await Mods.grantItems(this.ctxMgr.settings.playerName, targetName, coll.ids, give);
    if (!res.ok) {
      window.alert(
        res.error === "target_not_found"
          ? `Couldn't find "${targetName}".`
          : `Couldn't update ${coll.name} (${res.error ?? "error"}).`,
      );
      return;
    }
    if (this.target.kind === "self") void this.ctxMgr.shop.refresh();
  }

  /** Give or take EVERY collection's items at once (one atomic request). */
  private async setAllCollections(give: boolean): Promise<void> {
    const targetName =
      this.target.kind === "self" ? this.ctxMgr.settings.playerName : this.target.name;
    if (!targetName) {
      window.alert("No target set. Pick a target or log in.");
      return;
    }
    const verb = give ? "Give" : "Take";
    if (!window.confirm(`${verb} ALL collections ${give ? "to" : "from"} "${targetName}"?`)) return;
    const allIds = [...new Set(COLLECTIONS.flatMap((c) => c.ids))];
    const res = await Mods.grantItems(this.ctxMgr.settings.playerName, targetName, allIds, give);
    if (!res.ok) {
      window.alert(
        res.error === "target_not_found"
          ? `Couldn't find "${targetName}".`
          : `Couldn't update collections (${res.error ?? "error"}).`,
      );
      return;
    }
    if (this.target.kind === "self") void this.ctxMgr.shop.refresh();
  }

  private async setOwnedForTarget(unlock: boolean): Promise<void> {
    const targetName =
      this.target.kind === "self" ? this.ctxMgr.settings.playerName : this.target.name;
    if (!targetName) {
      window.alert("No target set. Pick a target or log in.");
      return;
    }
    const verb = unlock ? "Unlock ALL" : "Lock ALL";
    if (!window.confirm(`${verb} shop items for "${targetName}"?`)) return;
    const items = unlock ? CATALOG.map((it) => it.id) : [];
    const res = await Mods.setOwned(this.ctxMgr.settings.playerName, targetName, items);
    if (!res.ok) {
      window.alert(
        res.error === "target_not_found"
          ? `Couldn't find "${targetName}".`
          : `Couldn't update owned items (${res.error ?? "error"}).`,
      );
      return;
    }
    if (this.target.kind === "self") void this.ctxMgr.shop.refresh();
  }

  private async clearTargetMods(): Promise<void> {
    if (this.target.kind === "self") {
      const mods = this.ctxMgr.mods;
      mods.setFlags(emptyMods());
      mods.noclip = false;
      mods.startingScore = 0;
      mods.startingFlies = 0;
      this.rebuild();
      return;
    }
    this.target = {
      kind: "other",
      name: this.target.name,
      flags: emptyMods(),
      legacy: emptyLegacy(),
    };
    this.rebuild();
    await this.pushTarget();
  }

  private readonly onWheel = (e: WheelEvent): void => {
    if (this.maxScroll <= 0) return;
    e.preventDefault();
    this.scrollY = clamp(this.scrollY + e.deltaY, 0, this.maxScroll);
    this.rebuild();
  };

  private readonly onDragStart = (e: PointerEvent): void => {
    if (this.maxScroll <= 0) return;
    const { x, y } = this.renderer.clientToCanvas(e.clientX, e.clientY);
    if (x >= CARD_X && x <= CARD_X + PANEL_W && y >= HEADER_BOTTOM && y <= FOOTER_TOP) {
      this.dragLastY = e.clientY;
    }
  };

  private readonly onDragMove = (e: PointerEvent): void => {
    if (this.dragLastY === null) return;
    const dyClient = e.clientY - this.dragLastY;
    this.dragLastY = e.clientY;
    const rect = this.renderer.canvas.getBoundingClientRect();
    const dy = (dyClient / rect.height) * VIRTUAL_HEIGHT;
    this.scrollY = clamp(this.scrollY - dy, 0, this.maxScroll);
    this.rebuild();
  };

  private readonly onDragEnd = (): void => {
    this.dragLastY = null;
  };

  private rebuild(): void {
    const buttons: Button[] = [];

    // back
    buttons.push(backButton(() => this.onBackButton()));

    // The "Give Collection" sub-view: pick a target, then a collection to grant.
    if (this.view === "collections") {
      const tb = { x: CARD_X, y: 74, w: PANEL_W, h: 22 };
      buttons.push({
        ...tb,
        draw: (ctx, hit) => drawSmallButton(ctx, "Change target…", tb, hit),
        onPress: () => void this.chooseTarget(),
      });
      // Give / Take EVERY collection at once.
      const allGive = { x: CARD_X, y: 100, w: (PANEL_W - 6) / 2, h: 22 };
      buttons.push({
        ...allGive,
        draw: (ctx, hit) => drawPill(ctx, "Give ALL", allGive.x, allGive.y, allGive.w, allGive.h, hit, "#2e7d32"),
        onPress: () => void this.setAllCollections(true),
      });
      const allTake = { x: CARD_X + (PANEL_W + 6) / 2, y: 100, w: (PANEL_W - 6) / 2, h: 22 };
      buttons.push({
        ...allTake,
        draw: (ctx, hit) => drawPill(ctx, "Take ALL", allTake.x, allTake.y, allTake.w, allTake.h, hit, "#8e1a1a"),
        onPress: () => void this.setAllCollections(false),
      });
      const stride = ROW_H + ROW_GAP;
      this.maxScroll = Math.max(0, COLLECTIONS.length * stride - ROW_GAP - LIST_H);
      if (this.scrollY > this.maxScroll) this.scrollY = this.maxScroll;
      const btnW = 52;
      const gap = 4;
      const nameW = PANEL_W - 2 * btnW - 2 * gap;
      COLLECTIONS.forEach((coll, i) => {
        const y = HEADER_BOTTOM + i * stride - this.scrollY;
        if (y < HEADER_BOTTOM || y + ROW_H > HEADER_BOTTOM + LIST_H) return;
        buttons.push({
          x: CARD_X,
          y,
          w: nameW,
          h: ROW_H,
          draw: (ctx) => drawNameCard(ctx, coll.name, CARD_X, y, nameW, ROW_H),
          onPress: () => undefined,
        });
        const gx = CARD_X + nameW + gap;
        buttons.push({
          x: gx,
          y,
          w: btnW,
          h: ROW_H,
          draw: (ctx, hit) => drawPill(ctx, "Give", gx, y, btnW, ROW_H, hit, "#2e7d32"),
          onPress: () => void this.setCollection(coll, true),
        });
        const tx = gx + btnW + gap;
        buttons.push({
          x: tx,
          y,
          w: btnW,
          h: ROW_H,
          draw: (ctx, hit) => drawPill(ctx, "Take", tx, y, btnW, ROW_H, hit, "#8e1a1a"),
          onPress: () => void this.setCollection(coll, false),
        });
      });
      this.ctxMgr.buttons.set(buttons);
      return;
    }

    // target picker + mod clear
    const targetBtn = { x: CARD_X, y: 74, w: PANEL_W - 90, h: 22 };
    buttons.push({
      ...targetBtn,
      draw: (ctx, hit) => drawSmallButton(ctx, "Change target…", targetBtn, hit),
      onPress: () => void this.chooseTarget(),
    });
    const clearBtn = { x: CARD_X + PANEL_W - 84, y: 74, w: 84, h: 22 };
    buttons.push({
      ...clearBtn,
      draw: (ctx, hit) => drawSmallButton(ctx, "Clear mods", clearBtn, hit),
      onPress: () => void this.clearTargetMods(),
    });

    // shop unlock / lock
    const unlockBtn = { x: CARD_X, y: 102, w: (PANEL_W - 6) / 2, h: 22 };
    buttons.push({
      ...unlockBtn,
      draw: (ctx, hit) => drawSmallButton(ctx, "Unlock all", unlockBtn, hit),
      onPress: () => void this.setOwnedForTarget(true),
    });
    const lockBtn = { x: CARD_X + (PANEL_W + 6) / 2, y: 102, w: (PANEL_W - 6) / 2, h: 22 };
    buttons.push({
      ...lockBtn,
      draw: (ctx, hit) => drawSmallButton(ctx, "Lock all", lockBtn, hit),
      onPress: () => void this.setOwnedForTarget(false),
    });

    // Opens the separate Give Collection sub-menu.
    const giveBtn = { x: CARD_X, y: 130, w: PANEL_W, h: 22 };
    buttons.push({
      ...giveBtn,
      draw: (ctx, hit) => drawSmallButton(ctx, "Give Collection…", giveBtn, hit),
      onPress: () => this.openCollections(),
    });

    // legacy controls — target-aware so they apply to self or a remote victim.
    const lmods = this.ctxMgr.mods;
    const legacy: LegacyMods =
      this.target.kind === "other"
        ? this.target.legacy
        : {
            noclip: lmods.noclip,
            startingScore: lmods.startingScore,
            startingFlies: lmods.startingFlies,
          };
    const fixedRows: { label: string; status: string; on: boolean; onPress: () => void }[] = [
      {
        label: "Noclip",
        status: legacy.noclip ? "ON" : "off",
        on: legacy.noclip,
        onPress: () => void this.toggleNoclip(),
      },
      {
        label: `Starting score (${legacy.startingScore})`,
        status: "set",
        on: legacy.startingScore > 0,
        onPress: () => void this.promptInt("score"),
      },
      {
        label: `Starting flies (${legacy.startingFlies})`,
        status: "set",
        on: legacy.startingFlies > 0,
        onPress: () => void this.promptInt("flies"),
      },
      {
        label: "Reset leaderboard",
        status: "(legacy)",
        on: false,
        onPress: () => this.confirmResetLeaderboard(),
      },
      {
        label: "Reset player score",
        status: "(legacy)",
        on: false,
        onPress: () => this.confirmResetPlayerScore(),
      },
      {
        label: "Reset password",
        status: "(admin)",
        on: false,
        onPress: () => this.confirmResetPassword(),
      },
      {
        label: "Force rename",
        status: "(admin)",
        on: false,
        onPress: () => this.confirmForceRename(),
      },
      {
        label: "Ban player",
        status: "(admin)",
        on: false,
        onPress: () => this.confirmBan(true),
      },
      {
        label: "Unban player",
        status: "(admin)",
        on: false,
        onPress: () => this.confirmBan(false),
      },
    ];

    // Owner-only: manage who else has the mod menu.
    if (this.ctxMgr.mods.isAdmin(this.ctxMgr.settings.playerName)) {
      fixedRows.push(
        {
          label: "Give / Take Mod Menu",
          status: "(owner)",
          on: false,
          onPress: () => this.giveTakeModMenu(),
        },
        {
          label: "Take Mod Menu from all",
          status: "(owner)",
          on: false,
          onPress: () => this.revokeAllModMenus(),
        },
        {
          label: "Take granted items from all",
          status: "(owner)",
          on: false,
          onPress: () => this.revokeAllGrants(),
        },
      );
    }

    const listRows = fixedRows;

    // toggle rows
    const flags = this.currentFlags();
    const stride = ROW_H + ROW_GAP;
    const totalRows = listRows.length + MOD_KEYS.length;
    this.maxScroll = Math.max(0, totalRows * stride - ROW_GAP - LIST_H);
    if (this.scrollY > this.maxScroll) this.scrollY = this.maxScroll;

    listRows.forEach((row, i) => {
      const y = HEADER_BOTTOM + i * stride - this.scrollY;
      if (y < HEADER_BOTTOM || y + ROW_H > HEADER_BOTTOM + LIST_H) return;
      buttons.push(makeRowButton(row.label, row.status, row.on, CARD_X, y, PANEL_W, row.onPress));
    });
    MOD_KEYS.forEach((key, i) => {
      const idx = listRows.length + i;
      const y = HEADER_BOTTOM + idx * stride - this.scrollY;
      if (y < HEADER_BOTTOM || y + ROW_H > HEADER_BOTTOM + LIST_H) return;
      const label = MOD_LABELS[key];
      const on = flags[key];
      buttons.push(
        makeRowButton(label, on ? "ON" : "off", on, CARD_X, y, PANEL_W, () => void this.setFlag(key, !on)),
      );
    });

    this.ctxMgr.buttons.set(buttons);
  }

  private confirmResetLeaderboard(): void {
    if (!window.confirm("Reset the entire leaderboard? This cannot be undone.")) return;
    void this.ctxMgr.leaderboard
      .resetLeaderboard(this.ctxMgr.settings.playerName)
      .then((ok) => window.alert(ok ? "Leaderboard cleared." : "Failed to reset leaderboard."));
  }

  private confirmResetPlayerScore(): void {
    if (this.target.kind === "other") {
      const name = this.target.name;
      if (!window.confirm(`Reset "${name}"'s high score and global entry?`)) return;
      void this.ctxMgr.leaderboard
        .resetPlayer(this.ctxMgr.settings.playerName, name)
        .then((ok) =>
          window.alert(ok ? `"${name}"'s global entry reset.` : "Failed to reset player score."),
        );
      return;
    }
    if (!window.confirm("Reset YOUR high score and remove your global entry?")) return;
    this.ctxMgr.score.resetHigh();
    const name = this.ctxMgr.settings.playerName;
    if (!name) {
      window.alert("Local high score reset.");
      return;
    }
    void this.ctxMgr.leaderboard.resetPlayer(name, name).then((ok) =>
      window.alert(
        ok
          ? "Local high score & global entry reset."
          : "Local high score reset; failed to clear global entry.",
      ),
    );
  }

  private confirmResetPassword(): void {
    const targetName =
      this.target.kind === "self" ? this.ctxMgr.settings.playerName : this.target.name;
    if (!targetName) {
      window.alert("No target set. Pick a target or log in.");
      return;
    }
    // Admin sets a NEW password; the old one is never shown (it isn't stored
    // in plaintext). The server invalidates the target's current session.
    const pw = window.prompt(`Set a NEW password for "${targetName}" (4–64 chars):`, "");
    if (pw === null) return;
    if (pw.length < 4 || pw.length > 64) {
      window.alert("Password must be 4–64 characters.");
      return;
    }
    void Mods.resetPassword(this.ctxMgr.settings.playerName, targetName, pw).then((res) => {
      if (res.ok) {
        window.alert(`"${targetName}"'s password was reset. They'll need to log in again.`);
        return;
      }
      window.alert(
        res.error === "target_not_found"
          ? `Couldn't find "${targetName}".`
          : res.error === "invalid_password"
            ? "Password must be 4–64 characters."
            : `Couldn't reset password (${res.error ?? "error"}).`,
      );
    });
  }

  // Force the targeted player to pick a new name on their next poll. Their
  // progress is migrated by the rename, so nothing is lost. Self isn't a valid
  // target (you'd just rename yourself via the normal flow).
  private confirmForceRename(): void {
    if (this.target.kind !== "other") {
      window.alert("Pick a target player first (Change target…).");
      return;
    }
    const name = this.target.name;
    if (
      !window.confirm(
        `Force "${name}" to pick a new name?\n` +
          `They'll be prompted until they comply; their progress carries over.`,
      )
    ) {
      return;
    }
    void Mods.forceRename(this.ctxMgr.settings.playerName, name).then((res) => {
      if (res.ok) {
        window.alert(`"${name}" will be asked to choose a new name on their next poll.`);
        return;
      }
      window.alert(
        res.error === "target_not_found"
          ? `Couldn't find "${name}".`
          : res.error === "cannot_change_owner"
            ? "You can't force the owner to rename."
            : `Couldn't force rename (${res.error ?? "error"}).`,
      );
    });
  }

  // Ban or unban the targeted player. A real, enforced ban (not the fakeBanScreen
  // prank): the server blocks their login + run submissions and hides them from
  // the leaderboard, and their open session is locked into the BanScene. Self
  // isn't a valid target. The owner can't be banned (the server blocks it too).
  private confirmBan(ban: boolean): void {
    if (this.target.kind !== "other") {
      window.alert("Pick a target player first (Change target…).");
      return;
    }
    const name = this.target.name;
    if (
      !window.confirm(
        ban
          ? `Ban "${name}"?\n` +
              `They won't be able to log in or submit scores, they'll be removed from\n` +
              `the leaderboard, and any open session is locked out.`
          : `Unban "${name}"? They'll be able to log in and play again.`,
      )
    ) {
      return;
    }
    void Mods.setBan(this.ctxMgr.settings.playerName, name, ban).then((res) => {
      if (res.ok) {
        window.alert(`"${name}" has been ${ban ? "banned" : "unbanned"}.`);
        return;
      }
      window.alert(
        res.error === "target_not_found"
          ? `Couldn't find "${name}".`
          : res.error === "cannot_ban_owner"
            ? "You can't ban the owner."
            : `Couldn't ${ban ? "ban" : "unban"} (${res.error ?? "error"}).`,
      );
    });
  }

  private async promptInt(field: "score" | "flies"): Promise<void> {
    if (this.target.kind === "self") {
      const mods = this.ctxMgr.mods;
      const current = field === "score" ? mods.startingScore : mods.startingFlies;
      const raw = window.prompt(`Set starting ${field}:`, String(current));
      if (raw === null) return;
      const n = Math.max(0, Math.floor(Number(raw)) || 0);
      if (field === "score") mods.startingScore = n;
      else mods.startingFlies = n;
      this.rebuild();
      return;
    }
    const t = this.target;
    const prev = field === "score" ? t.legacy.startingScore : t.legacy.startingFlies;
    const raw = window.prompt(`Set ${t.name}'s starting ${field}:`, String(prev));
    if (raw === null) return;
    const n = Math.max(0, Math.floor(Number(raw)) || 0);
    if (field === "score") t.legacy.startingScore = n;
    else t.legacy.startingFlies = n;
    this.rebuild();
    const res = await this.pushTarget();
    if (!res.ok) {
      if (field === "score") t.legacy.startingScore = prev;
      else t.legacy.startingFlies = prev;
      this.rebuild();
      this.alertPushError(res.error, t.name);
    }
  }
}

function makeRowButton(
  label: string,
  status: string,
  on: boolean,
  x: number,
  y: number,
  w: number,
  onPress: () => void,
): Button {
  return {
    x, y, w, h: ROW_H,
    draw: (ctx, hit) => {
      ctx.fillStyle = on ? "#6b8e23" : hit ? "#7a5e2a" : "rgba(245,233,200,0.9)";
      fillRoundedRect(ctx, x, y, w, ROW_H, 6);
      ctx.strokeStyle = "#7a5e2a";
      ctx.lineWidth = 1.5;
      strokeRoundedRect(ctx, x + 0.5, y + 0.5, w - 1, ROW_H - 1, 6);
      ctx.fillStyle = on || hit ? "#f5e9c8" : "#1a1f2b";
      ctx.font = "13px system-ui, sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(label, x + 12, y + ROW_H / 2);
      ctx.textAlign = "right";
      ctx.font = "bold 11px system-ui, sans-serif";
      ctx.fillText(status, x + w - 12, y + ROW_H / 2);
      ctx.textBaseline = "alphabetic";
    },
    onPress,
  };
}

// Left-side name plate for a Give/Take collection row.
function drawNameCard(
  ctx: CanvasRenderingContext2D,
  name: string,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  ctx.fillStyle = "rgba(245,233,200,0.9)";
  fillRoundedRect(ctx, x, y, w, h, 6);
  ctx.strokeStyle = "#7a5e2a";
  ctx.lineWidth = 1.5;
  strokeRoundedRect(ctx, x + 0.5, y + 0.5, w - 1, h - 1, 6);
  ctx.fillStyle = "#1a1f2b";
  ctx.font = "13px system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(name, x + 10, y + h / 2);
  ctx.textBaseline = "alphabetic";
}

// Colored action pill (Give = green, Take = red).
function drawPill(
  ctx: CanvasRenderingContext2D,
  label: string,
  x: number,
  y: number,
  w: number,
  h: number,
  hit: boolean,
  color: string,
): void {
  ctx.fillStyle = hit ? "#1a1f2b" : color;
  fillRoundedRect(ctx, x, y, w, h, 6);
  ctx.fillStyle = "#fff";
  ctx.font = "bold 12px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, x + w / 2, y + h / 2);
  ctx.textBaseline = "alphabetic";
}

function drawSmallButton(
  ctx: CanvasRenderingContext2D,
  label: string,
  rect: { x: number; y: number; w: number; h: number },
  hit: boolean,
): void {
  ctx.fillStyle = hit ? "#7a5e2a" : "rgba(245,233,200,0.9)";
  fillRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, 6);
  ctx.strokeStyle = "#7a5e2a";
  ctx.lineWidth = 1.5;
  strokeRoundedRect(ctx, rect.x + 0.5, rect.y + 0.5, rect.w - 1, rect.h - 1, 6);
  ctx.fillStyle = hit ? "#f5e9c8" : "#1a1f2b";
  ctx.font = "12px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h / 2);
  ctx.textBaseline = "alphabetic";
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
